/**
 * features/recurring/RecurringEditor.tsx
 * Create or edit a recurring invoice template.
 */
import { useMemo, useState } from 'react';
import { X, RefreshCw, Trash2, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useClients } from '@/features/clients/useClients';
import { buildLineItems, computeTotals, formatPula } from '@/lib/money';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import type { RecurrenceFrequency, RecurringInvoice, VatTreatment } from '@/types';
import { createTemplate, updateTemplate, deleteTemplate, type TemplateLine } from './recurringWrite';

const FREQ: Array<{ value: RecurrenceFrequency; label: string }> = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function RecurringEditor({ template, onClose }: { template: RecurringInvoice | null; onClose: () => void }) {
  const { user } = useAuth();
  const { currentVatSnapshot } = useSettings();
  const { clients } = useClients();
  const actor = user?.email ?? user?.uid ?? 'unknown';
  const vat = currentVatSnapshot();

  const [clientId, setClientId] = useState(template?.clientId ?? '');
  const [frequency, setFrequency] = useState<RecurrenceFrequency>(template?.frequency ?? 'monthly');
  const [startDate, setStartDate] = useState(template?.startDate ?? todayISO());
  const [paymentTermDays, setPaymentTermDays] = useState(String(template?.paymentTermDays ?? 14));
  const [notes, setNotes] = useState(template?.notes ?? '');
  const [lines, setLines] = useState<TemplateLine[]>(
    template?.lineItems.map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice, vatTreatment: l.vatTreatment as VatTreatment })) ?? [
      { description: '', quantity: 1, unitPrice: 0, vatTreatment: 'standard' },
    ],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const validLines = lines.filter((l) => l.description.trim());
  const totals = useMemo(() => computeTotals(buildLineItems(validLines, vat)), [validLines, vat]);

  function setLine(idx: number, patch: Partial<TemplateLine>) {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  async function save() {
    if (!clientId) {
      setError('Choose a client.');
      return;
    }
    if (validLines.length === 0) {
      setError('Add at least one line.');
      return;
    }
    setBusy(true);
    setError('');
    const payload = {
      clientId,
      lineItems: validLines,
      frequency,
      startDate,
      paymentTermDays: Number(paymentTermDays) || 14,
      notes,
    };
    try {
      if (template) await updateTemplate(template.id, payload);
      else await createTemplate(payload, actor);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-10 w-full max-w-2xl">
        <div className={glassSurface('space-y-4 p-5')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <RefreshCw size={18} />
              <h2 className="text-base font-semibold text-white">{template ? 'Edit recurring invoice' : 'New recurring invoice'}</h2>
            </div>
            <button onClick={onClose} className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Client">
              <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">Select a client…</option>
                {clients.filter((c) => c.active).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Frequency">
              <Select value={frequency} onChange={(e) => setFrequency(e.target.value as RecurrenceFrequency)}>
                {FREQ.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="First / next run date">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Payment term (days)">
              <Input inputMode="numeric" value={paymentTermDays} onChange={(e) => setPaymentTermDays(e.target.value)} />
            </Field>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">Line items</div>
            {lines.map((l, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input className="flex-1" value={l.description} onChange={(e) => setLine(idx, { description: e.target.value })} placeholder="Description" />
                <Input className="w-16 text-right" inputMode="decimal" value={String(l.quantity)} onChange={(e) => setLine(idx, { quantity: Number(e.target.value) || 0 })} />
                <Input className="w-28 text-right" inputMode="decimal" value={String(l.unitPrice)} onChange={(e) => setLine(idx, { unitPrice: Number(e.target.value) || 0 })} />
                <button onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))} className="rounded-lg p-1.5 text-gray-400 hover:text-red-600" aria-label="Remove line">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            <Button variant="ghost" icon={<Plus size={15} />} onClick={() => setLines((ls) => [...ls, { description: '', quantity: 1, unitPrice: 0, vatTreatment: 'standard' }])}>
              Add line
            </Button>
          </div>

          <Field label="Notes (appear on each invoice)">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>

          <div className="flex items-center justify-between rounded-xl bg-brandSoft px-4 py-2.5 dark:bg-white/5">
            <span className="text-sm font-semibold text-primary dark:text-gray-100">Each invoice total</span>
            <span className="text-lg font-bold text-brand">{formatPula(totals.total)}</span>
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex items-center justify-between gap-2">
            <div>
              {template && (
                <Button variant="ghost" icon={<Trash2 size={16} />} onClick={() => { if (window.confirm('Delete this recurring template?')) { void deleteTemplate(template.id); onClose(); } }} disabled={busy}>
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={save} loading={busy}>
                Save template
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
